/**
 * Qué impresora es la de ESTE aparato, y qué comandas ya salieron por papel.
 *
 * Todo vive en el propio dispositivo y NADA de esto va al backend, por un motivo
 * técnico y no de pereza: en iOS el identificador de un dispositivo BLE es un UUID
 * por-app-y-por-aparato, no la MAC. El identificador que guarda el iPad de barra no
 * le sirve absolutamente a ningún otro iPad, así que sincronizarlo sería mover un
 * dato inútil. Cero migraciones, cero PR de backend.
 *
 * Se guarda además el par servicio+característica que funcionó: así, la impresora
 * de otro club es un DATO nuevo y no una versión nueva de la app.
 */

/** Comandas recordadas por local. Suficiente para varios turnos, y no engorda el almacén. */
export const MAX_IMPRESAS = 200;

/**
 * Comandas ya VISTAS por esta pantalla, para saber cuáles son nuevas de verdad.
 *
 * Vive en el dispositivo y no en memoria porque iOS purga la página cuando la app
 * pasa a segundo plano: al volver se recarga sola, y con el conjunto en memoria las
 * comandas que llegaron mientras tanto se tomaban por "ya estaban ahí" y no se
 * imprimían nunca. En una barra eso es mirar el móvil y perder los tickets.
 */
export const MAX_VISTAS = 300;

const claveImpresora = (venueId) => `wa:impresora:${venueId || ""}`;
const claveImpresas = (venueId) => `wa:impresas:${venueId || ""}`;
const claveVistas = (venueId) => `wa:vistas:${venueId || ""}`;

// localStorage revienta en navegación privada y con el almacén lleno. Nunca debe
// tumbar la Barra: sin memoria se imprime igual, solo se pierde el anti-duplicado.
function leer(clave) {
  try {
    const crudo = window.localStorage.getItem(clave);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

function escribir(clave, valor) {
  try {
    window.localStorage.setItem(clave, JSON.stringify(valor));
    return true;
  } catch {
    return false;
  }
}

export function leerEmparejamiento(venueId) {
  const guardado = leer(claveImpresora(venueId));
  return guardado && guardado.deviceId ? guardado : null;
}

export function guardarEmparejamiento(venueId, datos) {
  if (!datos || !datos.deviceId) return false;
  return escribir(claveImpresora(venueId), {
    deviceId: datos.deviceId,
    deviceName: datos.deviceName || "",
    servicio: datos.servicio || "",
    caracteristica: datos.caracteristica || "",
    actualizado: new Date().toISOString(),
  });
}

export function olvidarEmparejamiento(venueId) {
  try {
    window.localStorage.removeItem(claveImpresora(venueId));
    return true;
  } catch {
    return false;
  }
}

/**
 * ¿Salió ya esta comanda por papel?
 *
 * Es la defensa que sobrevive a una recarga: sin ella, refrescar la tablet con
 * comandas pendientes en pantalla las reimprimiría todas.
 */
export function yaImpresa(venueId, orderId) {
  if (!orderId) return false;
  const lista = leer(claveImpresas(venueId));
  return Array.isArray(lista) && lista.includes(String(orderId));
}

/** Apunta la comanda como impresa, tirando la más vieja al pasar de MAX_IMPRESAS. */
export function marcarImpresa(venueId, orderId) {
  if (!orderId) return false;
  const id = String(orderId);
  const previas = leer(claveImpresas(venueId));
  const lista = Array.isArray(previas) ? previas.filter((x) => x !== id) : [];
  lista.push(id);
  return escribir(claveImpresas(venueId), lista.slice(-MAX_IMPRESAS));
}

/** Identificadores ya vistos. Vacío significa que esta pantalla se estrena aquí. */
export function leerVistas(venueId) {
  const lista = leer(claveVistas(venueId));
  return Array.isArray(lista) ? lista : [];
}

export function guardarVistas(venueId, ids) {
  return escribir(claveVistas(venueId), ids.map(String).slice(-MAX_VISTAS));
}

/** Nombre de ESTE aparato como estación: "Móvil barra", "Tablet cocina". */
export function leerEtiqueta(venueId) {
  const guardada = leer(`wa:etiqueta:${venueId || ""}`);
  return typeof guardada === "string" && guardada.trim() ? guardada : "Barra";
}

export function guardarEtiqueta(venueId, etiqueta) {
  return escribir(`wa:etiqueta:${venueId || ""}`, String(etiqueta).trim().slice(0, 60) || "Barra");
}

/**
 * Comandas que esta ESTACIÓN ya consideró para imprimir.
 *
 * Aparte de las "vistas" de la pantalla a propósito: son dos preguntas distintas —
 * qué ha mostrado esta pantalla, y qué ha valorado esta estación. Una pantalla sin
 * impresora muestra de todo y no valora nada.
 *
 * Se guarda en el dispositivo porque el móvil purga la página al pasar a segundo
 * plano: sin esto, al volver se creería recién estrenada y no imprimiría lo que llegó
 * mientras tanto.
 */
export function leerBaseImpresion(venueId) {
  // `null` = esta estación no se ha estrenado aquí. Distinto de la lista VACÍA, que es
  // "ya se estrenó y la barra estaba sin comandas". Confundir las dos deja la estación
  // creyéndose recién llegada para siempre, y entonces no imprime nunca.
  const lista = leer(`wa:impresion-base:${venueId || ""}`);
  return Array.isArray(lista) ? lista : null;
}

export function guardarBaseImpresion(venueId, ids) {
  return escribir(`wa:impresion-base:${venueId || ""}`, ids.map(String).slice(-MAX_VISTAS));
}

/**
 * Comandas reclamadas cuya SUELTA no llegó al servidor.
 *
 * `reclamar` pone `printed_at` ANTES de imprimir —así dos aparatos no sacan el mismo
 * papel— y `soltar` lo deshace cuando la impresión falla. Si en ese momento también
 * falla la red, la comanda se queda marcada como IMPRESA sin haber salido: el ticket
 * fantasma. Nadie la vuelve a mirar, porque para el servidor ya está hecha.
 *
 * Se guarda en el dispositivo por el mismo motivo que la base: el móvil purga la página
 * al pasar a segundo plano, y una suelta pendiente que se pierda ahí es un fantasma
 * permanente.
 */
export function leerSueltasPendientes(venueId) {
  const lista = leer(`wa:impresion-soltar:${venueId || ""}`);
  return Array.isArray(lista) ? lista.map(String) : [];
}

export function guardarSueltasPendientes(venueId, ids) {
  return escribir(`wa:impresion-soltar:${venueId || ""}`,
    Array.from(new Set(ids.map(String))).slice(-MAX_VISTAS));
}
